import { __ } from '@wordpress/i18n';
import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  Route,
  Router,
} from 'react-router-dom';
import { createBrowserHistory } from 'history';

// Components.
import Filters from '../filters';
import GrantsActiveFilters from '../grantsActiveFilters';
import GrantsList from '../grantsList';
import GrantsStats from '../grantsStats';

// GIFs.
import LoaderGif from '../../images/ajax-loader.gif';

// Services.
import fetchAll from '../../js/services/fetch-all';
import filterGrants from '../../js/services/filters/filter-grants';
import filtersFromGrants from '../../js/services/filters/filters-from-grants';

// Styles.
import './grants.scss';

const Grants = () => {
  // Config.
  const loadMoreAmount = 10;

  // State.
  const [activeFilters, setActiveFilters] = useState([]);
  const [contentEndIndex, setContentEndIndex] = useState(loadMoreAmount);
  const [filters, setFilters] = useState([]);
  const [grants, setGrants] = useState([]);
  const [grantsFiltered, setGrantsFiltered] = useState([]);
  const [loading, setLoading] = useState(true);

  // URL history state.
  const history = createBrowserHistory();
  const initialGrantSlug = window.location.pathname.split('/grants/')[1] || '';

  /**
   * A helper function for deactivating an active filter.
   * @param {string} type - The type of filter to deactivate.
   * @param {string} value - The value of the filter to deactivate.
   */
  const deactivateFilter = (type, value) => {
    setActiveFilters(activeFilters.filter(({
      type: checkType,
      value: checkValue,
    }) => type !== checkType || value !== checkValue));
  };

  /**
   * Updates the filtered content list and list of available filters in response
   * to data loading or active filters being applied.
   * @param {array} nextGrants - The new grants array to filter.
   * @param {array} nextActiveFilters - The new active filters array to apply.
   */
  const updateFilters = (nextGrants, nextActiveFilters) => {
    const nextGrantsFiltered = filterGrants(
      nextGrants,
      nextActiveFilters
    );
    setGrantsFiltered(nextGrantsFiltered);
    setFilters(filtersFromGrants(nextGrantsFiltered));
    setContentEndIndex(loadMoreAmount);
  };

  // Update results when filters change.
  useEffect(() => updateFilters(grants, activeFilters), [activeFilters]);

  // Fetch grants on initial load.
  useEffect(() => {
    (async () => {
      const allPosts = await fetchAll(
        ['grants'],
        {
          grants: '/knight-foundation-app/v1/grants',
        }
      );
      const { grants: grantsData } = allPosts;
      setGrants(grantsData);
      updateFilters(grantsData, activeFilters);
      setLoading(false);
    })();
  }, []);

  // Handle loading state.
  if (loading) {
    return (
      <div className="grants__loading">
        <img src={LoaderGif} alt="" />
        { __('Loading...', 'knight-foundation-app') }
      </div>
    );
  }

  return (
    <Router history={history}>
      <Route
        path="/"
        render={({ location }) => (
          <div className="grants">
            <GrantsActiveFilters
              activeFilters={activeFilters}
              onFilterDeactivate={deactivateFilter}
            />
            <GrantsStats filteredGrants={grantsFiltered} />
            <div className="grants__wrapper">
              <Filters
                activeFilters={activeFilters}
                enableKeywordSearch
                enableResetButton
                filters={filters}
                onChange={setActiveFilters}
              />
              <GrantsList
                filteredGrants={grantsFiltered}
                grantsVisible={contentEndIndex}
                history={history}
                initialGrantSlug={initialGrantSlug}
                location={location}
              />
            </div>
            {contentEndIndex < grantsFiltered.length &&
            <div className="grants-filters__button--wrapper">
              <button
                className={classnames(
                  'grants-filters__button',
                  'grants-filters__button--type--load-more',
                  'knight-filters-filters__button',
                  'knight-filters-filters__button--type--load-more'
                )}
                onClick={
                  () => setContentEndIndex(contentEndIndex + loadMoreAmount)
                }
                type="button"
              >
                { __('Show More', 'knight-foundation-app') }
              </button>
            </div>
            }
          </div>
        )}
      />
    </Router>
  );
};

const appElement = document.getElementById('grants');
if (appElement) {
  ReactDOM.render(React.createElement(Grants, null), appElement);
}
